import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchFindings,
  type Finding,
  type FindingsResponse,
  type Severity,
} from '../api/findings';
import './FindingsPage.css';

type StatusFilter = 'active' | 'all' | 'resolved';

const SEVERITY_ORDER: Severity[] = [
  'URGENT',
  'ATTEND',
  'WATCH',
  'ROUTINE',
  'INFO',
];

function severityRank(s: Severity): number {
  const i = SEVERITY_ORDER.indexOf(s);
  return i === -1 ? 99 : i;
}

function formatRelativeTime(ms: number | null): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'in the future';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

export function FindingsPage() {
  const [resp, setResp] = useState<FindingsResponse | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [dimFilter, setDimFilter] = useState<string>('');
  const [sevFilter, setSevFilter] = useState<Severity | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchFindings({
      status: statusFilter,
      dimension: dimFilter || undefined,
      severity: sevFilter || undefined,
    })
      .then(setResp)
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dimFilter, sevFilter]);

  const sortedFindings = useMemo(() => {
    if (!resp) return [];
    return [...resp.findings].sort(
      (a, b) =>
        severityRank(a.severity) - severityRank(b.severity) ||
        (b.last_seen_ms ?? 0) - (a.last_seen_ms ?? 0),
    );
  }, [resp]);

  const dimensions = useMemo(() => {
    if (!resp) return [];
    return Object.keys(resp.by_dimension).sort();
  }, [resp]);

  return (
    <div className="findings-page">
      <header className="findings-page__header">
        <div>
          <div className="findings-page__title">Findings</div>
          <div className="findings-page__subtitle">
            {resp
              ? `${resp.count} ${statusFilter} · ` +
                Object.entries(resp.by_severity)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ')
              : '—'}
          </div>
        </div>
        <button
          type="button"
          className="findings-page__refresh"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </header>

      <div className="findings-page__filters">
        <label className="findings-page__filter">
          status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="active">active (ACTIVE + STABLE)</option>
            <option value="all">all</option>
            <option value="resolved">recently resolved (7d)</option>
          </select>
        </label>
        <label className="findings-page__filter">
          dimension
          <select
            value={dimFilter}
            onChange={(e) => setDimFilter(e.target.value)}
          >
            <option value="">all</option>
            {dimensions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="findings-page__filter">
          severity
          <select
            value={sevFilter}
            onChange={(e) => setSevFilter(e.target.value as Severity | '')}
          >
            <option value="">all</option>
            {SEVERITY_ORDER.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="findings-page__error">{error}</div>}

      {loading && !resp && (
        <div className="findings-page__status">Loading...</div>
      )}
      {resp && sortedFindings.length === 0 && (
        <div className="findings-page__status">
          No findings match the current filter.
        </div>
      )}

      <div className="findings-page__list">
        {sortedFindings.map((f) => (
          <FindingCard key={f.fingerprint} finding={f} />
        ))}
      </div>
    </div>
  );
}

function subjectLink(subjectKey: string): { to: string; label: string } | null {
  // page:<id> → Pages panel filtered to that id
  if (subjectKey.startsWith('page:')) {
    const id = subjectKey.slice('page:'.length);
    return { to: `/pages?id=${encodeURIComponent(id)}`, label: 'open page →' };
  }
  // source:<slug> → Sources panel filtered to that slug
  if (subjectKey.startsWith('source:')) {
    const slug = subjectKey.slice('source:'.length);
    return { to: `/sources?slug=${encodeURIComponent(slug)}`, label: 'open source →' };
  }
  // subdomain / domain / cluster keys could route to graph in future;
  // for now no dedicated panel target.
  return null;
}

function FindingCard({ finding }: { finding: Finding }) {
  const [expanded, setExpanded] = useState(false);
  const sevClass = `findings-page__card--${finding.severity.toLowerCase()}`;
  const ageStr = formatRelativeTime(finding.last_seen_ms);
  const link = subjectLink(finding.subject_key);

  return (
    <article className={`findings-page__card ${sevClass}`}>
      <div className="findings-page__card-head">
        <span className={`findings-page__sev findings-page__sev--${finding.severity.toLowerCase()}`}>
          {finding.severity}
        </span>
        <span className="findings-page__dim">{finding.dimension}</span>
        <span className="findings-page__kind">· {finding.kind}</span>
        <span className="findings-page__status-tag">{finding.status}</span>
        <span className="findings-page__age">last seen {ageStr}</span>
      </div>
      <div className="findings-page__headline">{finding.headline}</div>
      <div className="findings-page__suggestion">{finding.suggestion}</div>
      <div className="findings-page__card-foot">
        <span className="findings-page__subject" title="subject key">
          <code>{finding.subject_key}</code>
        </span>
        {link && (
          <Link to={link.to} className="findings-page__subject-link">
            {link.label}
          </Link>
        )}
        <button
          type="button"
          className="findings-page__expand"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Hide evidence' : `Show evidence (${finding.evidence.length})`}
        </button>
      </div>
      {expanded && (
        <pre className="findings-page__evidence">
          {JSON.stringify(finding.evidence, null, 2)}
        </pre>
      )}
    </article>
  );
}
